/* global globalThis */
// client/src/coms/e2ee/engine.mjs
// Drives one device's OlmMachine (matrix-sdk-crypto-wasm: Olm for device-to-device key exchange, Megolm for room
// messages, cross-signing for identities, the m.megolm_backup.v1 key backup, and vodozemac's PkEncryption for the
// sealed recovery bundle). No cryptography is implemented here: this file only moves the library's requests to the
// Voxemera key endpoints, feeds the responses back, and refuses to encrypt when the library reports a trust problem.
// It has no imports, so the browser (cryptoSession.js) and the Node integration test (server/test/e2eeDm.test.js)
// run exactly the same code with their own `sdk` and `transport`.
//
// transport: { uploadKeys, queryKeys, claimKeys, sendToDevice(eventType, txnId, body), sync(since),
//              uploadSigningKeys(body, { reset }), uploadSignatures(body), createBackupVersion(body),
//              backupKeys(version, body), getBackupKeys(version), getRecovery(), putRecovery(body) }
//   each resolves to the parsed JSON response or throws (err.code is the server's error code when there is one).
// tokens:    { get(): string, set(v) } persists the sync position (so queued room keys survive reconnects).
// seen:      { get(k), set(k, v) } remembers which message first carried each ciphertext, so a ciphertext the
//            server stores again as a different message is reported as a replay (the library does not check this).
// keyOrigins: { get(k), set(k, v) } remembers which device delivered each Megolm session to this device over Olm
//            (an authenticated channel), so history restored from the key backup can be attributed once the
//            sender's device has also shared that session directly (see decrypt).
//
// Trust model: room keys are only shared with devices signed by their owner's cross-signing identity
// (identity-based strategy), so a device the server adds on its own never receives keys. A contact's identity is
// trusted on first use and pinned; if it changes, nothing is encrypted to them until the user accepts the change
// (and a contact the user verified must be re-verified).

const BACKUP_ALG = 'm.megolm_backup.v1.curve25519-aes-sha2';

async function digest(text) {
  const bytes = new TextEncoder().encode(String(text));
  const hash = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
}

function codedError(code, message, extra = {}) {
  return Object.assign(new Error(message), { code }, extra);
}

// A cross-signing key object's public key ({ keys: { 'ed25519:<pub>': '<pub>' } }), shown as a safety code.
export function keyFingerprint(json) {
  try {
    const obj = typeof json === 'string' ? JSON.parse(json) : json;
    const pub = Object.values((obj && obj.keys) || {})[0] || '';
    return String(pub).replace(/(.{4})/g, '$1 ').trim();
  } catch {
    return '';
  }
}

export function createE2eeEngine({ sdk, transport, userId, deviceId, storeName, storePassphrase, tokens, seen, keyOrigins, log = () => {} }) {
  let machine = null;
  let queue = Promise.resolve();
  const memorySeen = new Map();
  const ledger = seen || { get: (k) => memorySeen.get(k), set: (k, v) => memorySeen.set(k, v) };
  const memoryOrigins = new Map();
  const origins = keyOrigins || { get: (k) => memoryOrigins.get(k), set: (k, v) => memoryOrigins.set(k, v) };
  const originKey = (roomId, sessionId) => `${roomId}|${sessionId}`;
  const room = (id) => new sdk.RoomId(id);
  const users = (ids) => ids.map((u) => new sdk.UserId(u));
  const me = () => new sdk.UserId(userId);

  // OlmMachine calls must not interleave: everything goes through one queue.
  function serial(fn) {
    const run = queue.then(fn, fn);
    queue = run.catch(() => {});
    return run;
  }

  async function send(req) {
    switch (req.type) {
      case sdk.RequestType.KeysUpload:
        return transport.uploadKeys(JSON.parse(req.body));
      case sdk.RequestType.KeysQuery:
        return transport.queryKeys(JSON.parse(req.body));
      case sdk.RequestType.KeysClaim:
        return transport.claimKeys(JSON.parse(req.body));
      case sdk.RequestType.ToDevice:
        return transport.sendToDevice(req.event_type, req.txn_id, JSON.parse(req.body));
      case sdk.RequestType.SignatureUpload:
        return transport.uploadSignatures(JSON.parse(req.body));
      case sdk.RequestType.KeysBackup:
        return transport.backupKeys(req.version, JSON.parse(req.body));
      default:
        return null; // room messages (in-room verification): not used
    }
  }

  async function flushNow() {
    for (let round = 0; round < 6; round += 1) {
      const reqs = await machine.outgoingRequests();
      if (!reqs.length) return;
      for (const r of reqs) {
        const resp = await send(r);
        if (resp) await machine.markRequestAsSent(r.id, r.type, JSON.stringify(resp));
      }
    }
  }

  async function backupNow() {
    if (!(await machine.isBackupEnabled())) return;
    for (let round = 0; round < 20; round += 1) {
      const req = await machine.backupRoomKeys();
      if (!req) return;
      await machine.markRequestAsSent(req.id, req.type, JSON.stringify(await send(req)));
    }
  }

  // Room keys that arrived over Olm (m.room_key only: forwarded keys and backups prove nothing about the original
  // sender). The Olm channel authenticates the sending device's Curve25519 key (the library reports it; it may not
  // name the device yet, so the device is looked up by that key when a message is shown). The session id is the
  // Megolm session's Ed25519 signing key, so every message that decrypts under that session was signed by it.
  async function noteKeyOrigins(processed) {
    for (const p of processed || []) {
      try {
        if (p.type !== sdk.ProcessedToDeviceEventType.Decrypted) continue;
        const ev = JSON.parse(p.rawEvent);
        if (!ev || ev.type !== 'm.room_key' || !ev.content || !ev.content.room_id || !ev.content.session_id) continue;
        const info = p.encryptionInfo;
        if (!info || !info.senderCurve25519Key) continue;
        const k = originKey(ev.content.room_id, ev.content.session_id);
        if (await origins.get(k)) continue;
        await origins.set(k, { sender: info.sender.toString(), curve: info.senderCurve25519Key });
      } catch (e) {
        log('could not read a received room key', e && e.message);
      }
    }
  }

  // The sender's device (as the library knows it from the signed device list) holding this Curve25519 key.
  async function deviceByCurveKey(mx, curve) {
    const devices = await machine.getUserDevices(new sdk.UserId(mx));
    for (const dev of devices.devices()) {
      if (dev.curve25519Key && dev.curve25519Key.toBase64() === curve) return dev.deviceId.toString();
    }
    return '';
  }

  async function syncNow() {
    for (let round = 0; round < 10; round += 1) {
      const since = (tokens && tokens.get()) || '';
      const r = await transport.sync(since);
      const changed = users((r.device_lists && r.device_lists.changed) || []);
      const counts = new Map(Object.entries(r.device_one_time_keys_count || {}));
      const fallback = new Set(r.device_unused_fallback_key_types || []);
      const processed = await machine.receiveSyncChanges(JSON.stringify((r.to_device && r.to_device.events) || []), new sdk.DeviceLists(changed, []), counts, fallback);
      await noteKeyOrigins(processed);
      if (tokens) tokens.set(r.next_batch);
      await flushNow();
      if (!r.more) break;
    }
    await backupNow().catch((e) => log('backup failed', e && e.message));
  }

  async function ownTrustNow() {
    const status = await machine.crossSigningStatus();
    const identity = status.hasMaster && status.hasSelfSigning && status.hasUserSigning;
    const dev = await machine.getDevice(me(), new sdk.DeviceId(deviceId));
    return { identity, deviceSigned: !!(dev && dev.isCrossSignedByOwner()) };
  }

  async function identityNow(mx) {
    const id = await machine.getIdentity(new sdk.UserId(mx));
    if (!id) return { exists: false, verified: false, changed: false, violation: false, fingerprint: '' };
    const own = mx === userId;
    return {
      exists: true,
      verified: id.isVerified(),
      // A pinned identity was replaced (unverified contact), or a verified contact's identity changed.
      changed: own ? false : id.identityNeedsUserApproval(),
      violation: id.hasVerificationViolation(),
      fingerprint: keyFingerprint(id.masterKey),
    };
  }

  async function shareNow(roomId, members) {
    const trust = await ownTrustNow();
    if (!trust.identity) throw codedError('identity_missing', 'Your encryption identity is not set up on this device');
    if (!trust.deviceSigned) throw codedError('device_unverified', 'This device is not verified with your encryption identity yet');
    await machine.updateTrackedUsers(users(members));
    await flushNow();
    for (const mx of members) {
      if (mx === userId) continue;
      const s = await identityNow(mx);
      if (!s.exists) throw codedError('identity_unknown', 'The other participant has no encryption identity yet', { user: mx });
      if (s.changed || s.violation) throw codedError('identity_changed', 'The other participant’s encryption identity changed', { user: mx });
    }
    const claim = await machine.getMissingSessions(users(members));
    if (claim) await machine.markRequestAsSent(claim.id, claim.type, JSON.stringify(await send(claim)));
    const settings = new sdk.EncryptionSettings();
    // Only devices cross-signed by their owner get room keys; verified contacts whose identity changed are refused.
    settings.sharingStrategy = sdk.CollectStrategy.identityBasedStrategy();
    const reqs = await machine.shareRoomKey(room(roomId), users(members), settings);
    for (const r of reqs) await machine.markRequestAsSent(r.id, r.type, JSON.stringify(await send(r)));
  }

  async function uploadBootstrap(reqs, reset) {
    if (reqs.uploadKeysRequest) {
      const r = reqs.uploadKeysRequest;
      await machine.markRequestAsSent(r.id, r.type, JSON.stringify(await send(r)));
    }
    await transport.uploadSigningKeys(JSON.parse(reqs.uploadSigningKeysRequest.body), { reset });
    await transport.uploadSignatures(JSON.parse(reqs.uploadSignaturesRequest.body));
  }

  // Creates a fresh backup key and a new backup version, then seals every secret to a new recovery key.
  async function newRecoveryNow() {
    const backupKey = sdk.BackupDecryptionKey.createRandomKey();
    const pub = backupKey.megolmV1PublicKey.publicKeyBase64;
    const { version } = await transport.createBackupVersion({ algorithm: BACKUP_ALG, auth_data: { public_key: pub } });
    await machine.saveBackupDecryptionKey(backupKey, String(version));
    await machine.enableBackupV1(pub, String(version));
    const bundle = await machine.exportSecretsBundle();
    const recoverySecret = sdk.Curve25519SecretKey.new();
    const recoveryKey = recoverySecret.toBase64();
    const recoveryPub = sdk.PkDecryption.fromKey(sdk.Curve25519SecretKey.fromBase64(recoveryKey)).publicKey();
    const sealed = sdk.PkEncryption.fromKey(recoveryPub).encryptString(JSON.stringify(bundle.to_json())).toBase64();
    await transport.putRecovery({ public_key: recoveryPub.toBase64(), sealed: { ciphertext: sealed.ciphertext, mac: sealed.mac, ephemeralKey: sealed.ephemeralKey }, backup_version: String(version) });
    await backupNow();
    return recoveryKey;
  }

  // Fetches the current keys of these accounts now (e.g. to see a signature that was just uploaded).
  async function queryNow(mxIds) {
    await machine.updateTrackedUsers(users(mxIds));
    const q = machine.queryKeysForUsers(users(mxIds));
    await machine.markRequestAsSent(q.id, q.type, JSON.stringify(await send(q)));
    await flushNow();
  }

  // Re-reads this account's own keys, so the machine sees signatures that were just uploaded.
  async function refreshOwnNow() {
    await queryNow([userId]);
  }

  async function signOwnDeviceNow() {
    await refreshOwnNow();
    const dev = await machine.getDevice(me(), new sdk.DeviceId(deviceId));
    if (!dev) throw codedError('device_missing', 'This device was not found');
    await transport.uploadSignatures(JSON.parse((await dev.verify()).body));
    await refreshOwnNow();
  }

  // Returns { restored, skipped, missing }: skipped = backed-up keys that did not decrypt (damaged entries), which
  // are left alone so nothing already on this device is lost; missing = there is no backup to restore from.
  async function restoreBackupNow(decryptionKeyB64, version) {
    const key = sdk.BackupDecryptionKey.fromBase64(decryptionKeyB64);
    let data;
    try {
      data = await transport.getBackupKeys(version);
    } catch (e) {
      if (e && (e.status === 404 || e.code === 'M_NOT_FOUND')) return { restored: 0, skipped: 0, missing: true };
      throw e;
    }
    const byRoom = new Map();
    let skipped = 0;
    for (const [roomId, r] of Object.entries((data && data.rooms) || {})) {
      const sessions = new Map();
      for (const [sid, s] of Object.entries((r && r.sessions) || {})) {
        try {
          const sd = s.session_data;
          const session = JSON.parse(key.decryptV1(sd.ephemeral, sd.mac, sd.ciphertext));
          if (!session || typeof session.session_key !== 'string') throw new Error('not a room key');
          sessions.set(sid, session);
        } catch (e) {
          skipped += 1;
          log('skipped a backed-up key that did not decrypt', e && e.message);
        }
      }
      if (sessions.size) byRoom.set(room(roomId), sessions);
    }
    if (!byRoom.size) return { restored: 0, skipped, missing: false };
    const result = await machine.importBackedUpRoomKeys(byRoom, () => {}, String(version));
    return { restored: Number(result.importedCount || 0), skipped, missing: false };
  }

  return {
    get deviceId() {
      return deviceId;
    },
    start: () =>
      serial(async () => {
        machine = await sdk.OlmMachine.initialize(me(), new sdk.DeviceId(deviceId), storeName || undefined, storePassphrase || undefined);
        await flushNow(); // publishes identity keys and one-time keys
      }),
    sync: () => serial(syncNow),
    // { identity: this device holds the account's cross-signing keys, deviceSigned: this device is signed by them }
    ownTrust: () => serial(ownTrustNow),
    identity: (mx) =>
      serial(async () => {
        await machine.updateTrackedUsers([new sdk.UserId(mx)]);
        await syncNow(); // picks up identity and device changes announced since the last sync
        return identityNow(mx);
      }),
    // First device of an account (or an explicit reset): creates the cross-signing identity, signs this device,
    // sets up the key backup and returns the new recovery key. The caller must show it to the user exactly once.
    bootstrap: ({ reset = false } = {}) =>
      serial(async () => {
        await uploadBootstrap(await machine.bootstrapCrossSigning(!!reset), !!reset);
        await refreshOwnNow();
        return newRecoveryNow();
      }),
    // A further device: opens the recovery bundle with the user's recovery key, imports the identity and backup
    // keys, signs this device and restores the backed-up room keys (message history).
    restore: (recoveryKey) =>
      serial(async () => {
        let rec;
        try {
          rec = await transport.getRecovery();
        } catch (e) {
          if (e && (e.status === 404 || e.code === 'M_NOT_FOUND')) throw codedError('recovery_missing', 'Your account has no recovery key set up, so this device cannot be verified with one');
          throw e;
        }
        let opened;
        try {
          const secret = sdk.Curve25519SecretKey.fromBase64(String(recoveryKey || '').replace(/\s+/g, ''));
          const s = rec.sealed || {};
          opened = sdk.PkDecryption.fromKey(secret).decryptString(sdk.PkMessage.fromBase64(new sdk.Base64EncodedPkMessage(s.ciphertext, s.mac, s.ephemeralKey)));
        } catch {
          throw codedError('recovery_key_wrong', 'That recovery key does not open your encryption secrets');
        }
        let bundleJson;
        try {
          bundleJson = JSON.parse(opened);
        } catch {
          throw codedError('recovery_damaged', 'Your stored encryption secrets are damaged');
        }
        // Nothing on this device changes until the bundle has opened, parsed and the account's published identity
        // is known (the library then checks the imported private keys against it).
        await refreshOwnNow();
        const published = await machine.getIdentity(me());
        const bundleMaster = bundleJson && bundleJson.cross_signing && bundleJson.cross_signing.master_key;
        if (!published || !bundleMaster) throw codedError('recovery_damaged', 'Your stored encryption secrets are damaged');
        await machine.importSecretsBundle(sdk.SecretsBundle.from_json(bundleJson));
        const backup = bundleJson.backup;
        if (backup && backup.key && backup.backup_version) {
          const key = sdk.BackupDecryptionKey.fromBase64(backup.key);
          await machine.saveBackupDecryptionKey(key, String(backup.backup_version));
          await machine.enableBackupV1(key.megolmV1PublicKey.publicKeyBase64, String(backup.backup_version));
        }
        await signOwnDeviceNow();
        const r = backup && backup.key ? await restoreBackupNow(backup.key, backup.backup_version) : { restored: 0, skipped: 0, missing: true };
        return { ...r, fingerprint: keyFingerprint((await machine.getIdentity(me())).masterKey) };
      }),
    // "I compared their safety code with them": signs their identity with this account's user-signing key.
    verifyUser: (mx) =>
      serial(async () => {
        const id = await machine.getIdentity(new sdk.UserId(mx));
        if (!id) throw codedError('identity_unknown', 'No encryption identity to verify');
        await transport.uploadSignatures(JSON.parse((await id.verify()).body));
        await queryNow([mx]);
        return identityNow(mx);
      }),
    // The user accepts a contact's new identity after being warned (it is pinned; any verification is withdrawn).
    acceptIdentityChange: (mx) =>
      serial(async () => {
        const id = await machine.getIdentity(new sdk.UserId(mx));
        if (!id) return identityNow(mx);
        if (id.hasVerificationViolation()) await id.withdrawVerification();
        if (id.identityNeedsUserApproval && id.identityNeedsUserApproval()) await id.pinCurrentMasterKey();
        return identityNow(mx);
      }),
    // Makes sure every trusted device of the members holds this device's room key for the conversation.
    prepare: (roomId, members) =>
      serial(async () => {
        await syncNow();
        await shareNow(roomId, members);
      }),
    encrypt: (roomId, members, text) =>
      serial(async () => {
        await syncNow();
        await shareNow(roomId, members);
        const out = await machine.encryptRoomEvent(room(roomId), 'm.room.message', JSON.stringify({ msgtype: 'm.text', body: String(text) }));
        await backupNow().catch(() => {});
        return out;
      }),
    // Throws when the message cannot be decrypted (missing keys, tampering, replay) or was not sent by `sender`.
    // syncOnMiss: fetch queued room keys and retry once when the key is missing (callers decrypting a whole page
    // sync once first and pass false, so a channel full of undecryptable history does not sync per message).
    decrypt: (roomId, { ciphertext, sender, eventId, ts }, { syncOnMiss = true } = {}) =>
      serial(async () => {
        let content;
        try {
          content = JSON.parse(ciphertext);
        } catch {
          throw codedError('undecryptable', 'This encrypted message is damaged');
        }
        const ev = { type: 'm.room.encrypted', event_id: eventId, sender, origin_server_ts: ts || 0, room_id: roomId, content };
        let d;
        try {
          d = await machine.decryptRoomEvent(JSON.stringify(ev), room(roomId), new sdk.DecryptionSettings(sdk.TrustRequirement.Untrusted));
        } catch (first) {
          if (!syncOnMiss) throw codedError('no_key', 'This device has no key for this message');
          // The room key may still be queued for this device: fetch it once and retry.
          await syncNow();
          try {
            d = await machine.decryptRoomEvent(JSON.stringify(ev), room(roomId), new sdk.DecryptionSettings(sdk.TrustRequirement.Untrusted));
          } catch {
            throw codedError('no_key', 'This device has no key for this message');
          }
        }
        const shield = d.shieldState(false);
        // Keys restored from the key backup carry no proof of which device created them (anyone who knows the
        // public backup key, including the server, could have added one), so such messages are shown as
        // "sender not confirmed", never as fully trusted...
        let fromBackup = !d.senderDevice && shield.code === sdk.ShieldStateCode.AuthenticityNotGuaranteed;
        let senderDevice = d.senderDevice ? d.senderDevice.toString() : '';
        // ...unless the sending device has also delivered this very session to this device over Olm. The session id
        // is the session's signing key and every message is signed with it, so the backup copy and the direct copy
        // are the same session, and all of its messages (earlier ones included) come from that device.
        if (fromBackup && content.session_id) {
          const o = await origins.get(originKey(roomId, content.session_id));
          const viaOlm = o && o.sender === sender && o.curve && o.curve === d.senderCurve25519Key ? await deviceByCurveKey(sender, o.curve) : '';
          if (viaOlm) {
            fromBackup = false;
            senderDevice = viaOlm;
          }
        }
        // Otherwise the Megolm session must belong to a known device of the account the message is shown from.
        if (d.sender.toString() !== sender || (!senderDevice && !fromBackup) || shield.code === sdk.ShieldStateCode.MismatchedSender || shield.code === sdk.ShieldStateCode.UnknownDevice) {
          throw codedError('sender_mismatch', 'The encrypted message was not sent by the account shown');
        }
        // Replay identity = the cryptographic message (Codex F07), not the JSON text around it: re-serialising the
        // same encrypted object (whitespace, key order) must not mint a new ledger key. The Megolm session id plus
        // its authenticated ciphertext (which carries the message index) identify the message; neither can be
        // altered without breaking decryption, and the room id binds it to this conversation.
        const key = await digest(JSON.stringify([roomId, String(content.session_id || ""), String(content.ciphertext || "")]));
        const first = await ledger.get(key);
        if (first && first !== eventId) throw codedError('replay', 'This encrypted message is a copy of an earlier one');
        if (!first) await ledger.set(key, eventId);
        const plain = JSON.parse(d.event);
        // Verified = the sending device is signed by its owner's identity and the user verified that identity
        // (both read from the library's current state, not from the message's cached shield).
        const dev = senderDevice ? await machine.getDevice(new sdk.UserId(sender), new sdk.DeviceId(senderDevice)) : null;
        const ident = await machine.getIdentity(new sdk.UserId(sender));
        const deviceSigned = !!(dev && dev.isCrossSignedByOwner());
        return {
          body: String((plain.content && plain.content.body) || ''),
          senderDevice,
          // Sent by a device of a contact you verified (or your own verified identity).
          verified: !fromBackup && deviceSigned && !!(ident && ident.isVerified()),
          fromBackup,
          // The sending device is not signed by its owner's identity.
          unsignedDevice: !fromBackup && !deviceSigned,
        };
      }),
    close: () =>
      serial(async () => {
        if (machine) machine.close();
        machine = null;
        log('closed');
      }),
  };
}
